'use client';

import { startTransition, useEffect, useState } from 'react';

import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { InstallmentCalculatorDialog } from '@packages/installment-calculator/src/InstallmentCalculatorDialog/InstallmentCalculatorDialog.client';
import { useInstallmentCalculatorTracking } from '../../../Tracking/hooks/useInstallmentCalculatorTracking/useInstallmentCalculatorTracking';
import { SideBySideWithTipBadge } from '../SideBySideWithTipBadge/SideBySideWithTipBadge.client';
import { SideBySideWithIconBadge } from '../SideBySideWithIconBadge/SideBySideWithIconBadge.client';
import { IconInstallmentsAndLegalHints } from '../IconInstallmentsAndLegalHints/IconInstallmentsAndLegalHints.client';

export type InstallmentCalculatorInDialogProps = {
  /**
   * the single installment value from the calculation result as integer (e.g. 1337)
   */
  singleInstallment: number;
  /**
   * the currency string from the calculation result
   */
  currency: string;
  /**
   * the number of installments from the calculation result
   */
  numberOfInstallments: number;
  /**
   * Product price value as int (e.g. 1337)
   */
  basePrice: number;
};

export const InstallmentCalculatorInDialog = ({
  singleInstallment,
  currency,
  numberOfInstallments,
  basePrice,
}: InstallmentCalculatorInDialogProps) => {
  const [dialogOpened, setDialogOpened] = useState(false);
  const {
    installmentCalculator: { InstallmentCalculatorCallToActionLayout },
  } = useConfig();

  const { trackGlycerinDisplayLink } = useInstallmentCalculatorTracking();

  useEffect(() => {
    trackGlycerinDisplayLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <InstallmentCalculatorDialog
        basePrice={basePrice}
        isOpen={dialogOpened}
        onClose={() => startTransition(() => setDialogOpened(false))}
      />
      {(() => {
        switch (InstallmentCalculatorCallToActionLayout?.layout) {
          case 'SideBySideWithTipBadge': {
            return (
              <SideBySideWithTipBadge
                singleInstallment={singleInstallment}
                currency={currency}
                numberOfInstallments={numberOfInstallments}
                openInstallmentCalculatorHandler={() => {
                  setDialogOpened(true);
                }}
              />
            );
          }
          case 'SideBySideWithIconBadge': {
            return (
              <SideBySideWithIconBadge
                singleInstallment={singleInstallment}
                currency={currency}
                numberOfInstallments={numberOfInstallments}
                openInstallmentCalculatorHandler={() =>
                  startTransition(() => setDialogOpened(true))
                }
              />
            );
          }
          case 'IconInstallmentsAndLegalHints': {
            return (
              <IconInstallmentsAndLegalHints
                singleInstallment={singleInstallment}
                currency={currency}
                openInstallmentCalculatorHandler={() =>
                  startTransition(() => setDialogOpened(true))
                }
              />
            );
          }
          default:
            return null;
        }
      })()}
    </>
  );
};
